import React from 'react';
import PropTypes from 'prop-types';
import Favicon from 'react-favicon';

import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import { ActionCodeOperation } from 'firebase/auth';

import { SYSTEM_RESOURCE } from '../../constants/EndPoints';
import * as misc from '../../constants/Misc';

import './Login.scss';

function parseQuery(location) {
  const query = (location && location.query) || {};
  const search = (location && location.search) || '';
  const params = new URLSearchParams(search);

  return {
    mode: query.mode || params.get('mode') || '',
    oobCode: query.oobCode || params.get('oobCode') || '',
    apiKey: query.apiKey || params.get('apiKey') || '',
    continueUrl: query.continueUrl || params.get('continueUrl') || '',
  };
}

function redact(value) {
  if (!value) {
    return '';
  }

  const str = String(value);
  if (str.length <= 8) {
    return '***';
  }

  return `${str.slice(0, 4)}…${str.slice(-4)}`;
}

function logFirebaseError(label, error) {
  console.error(label, {
    code: error && error.code,
    message: error && error.message,
    name: error && error.name,
    stack: error && error.stack,
    customData: error && error.customData,
  });
}

function maskPhoneNumber(phoneNumber) {
  if (!phoneNumber) {
    return '';
  }

  var digits = 0;
  for (var i = 0; i < phoneNumber.length; i += 1) {
    if (/\d/.test(phoneNumber.charAt(i))) {
      digits += 1;
    }
  }

  var digitsToHide = Math.max(0, digits - 4);
  var seenDigits = 0;

  return phoneNumber.replace(/\d/g, function (digit) {
    seenDigits += 1;
    return seenDigits <= digitsToHide ? '*' : digit;
  });
}

function pickMultiFactorInfo(actionCodeInfo) {
  if (!actionCodeInfo || !actionCodeInfo.data) {
    return null;
  }

  var info =
    actionCodeInfo.data.multiFactorInfo ||
    actionCodeInfo.data.multiFactorEnrollment ||
    actionCodeInfo.data.mfaInfo;
  if (Array.isArray(info)) {
    return info[0] || null;
  }
  return info || null;
}

function summarizeFactor(info) {
  if (!info) {
    return 'this second factor';
  }

  var displayName = info.displayName && info.displayName.trim();
  var phoneNumber = info.phoneNumber && info.phoneNumber.trim();

  if (displayName && phoneNumber) {
    return displayName + ' (' + maskPhoneNumber(phoneNumber) + ')';
  }

  if (displayName) {
    return displayName;
  }

  if (phoneNumber) {
    return maskPhoneNumber(phoneNumber);
  }

  if (info.factorId === 'phone') {
    return 'this phone number';
  }

  if (info.factorId === 'totp') {
    return 'this authenticator app';
  }

  return 'this second factor';
}

function friendlyError(error) {
  switch (error && error.code) {
    case 'auth/expired-action-code':
      return 'This confirmation link expired. Please ask for a new MFA removal email.';
    case 'auth/invalid-action-code':
      return 'This confirmation link is invalid or has already been used.';
    case 'auth/operation-not-allowed':
      return 'This action is not available for this account.';
    case 'auth/too-many-requests':
      return 'Too many attempts were made. Please wait a moment and try again.';
    default:
      return (
        (error && error.message) ||
        'We could not verify this confirmation link.'
      );
  }
}

function safeContinuePath(rawTarget) {
  if (!rawTarget) {
    return null;
  }

  try {
    var url = new URL(rawTarget, window.location.origin);
    if (url.origin !== window.location.origin) {
      return null;
    }
    return url.pathname + url.search + url.hash;
  } catch (error) {
    return null;
  }
}

async function fetchActionCodeData(query) {
  const apiKey = query.apiKey || process.env.REACT_APP_API_KEY;
  const endpoint = `https://identitytoolkit.googleapis.com/v1/accounts:resetPassword?key=${encodeURIComponent(
    apiKey
  )}`;

  console.info('MFA confirm-removal validating action code via REST', {
    apiKey: redact(apiKey),
    oobCode: redact(query.oobCode),
    endpoint:
      'https://identitytoolkit.googleapis.com/v1/accounts:resetPassword',
  });

  const response = await fetch(endpoint, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ oobCode: query.oobCode }),
  });

  const text = await response.text();
  console.info('MFA confirm-removal REST validation response', {
    ok: response.ok,
    status: response.status,
    body: text,
  });
  let body = null;

  try {
    body = JSON.parse(text);
  } catch (error) {
    body = null;
  }

  if (!response.ok) {
    const firebaseMessage = body && body.error && body.error.message;
    const error = new Error(
      firebaseMessage || text || 'Firebase request failed'
    );
    error.code = firebaseMessage
      ? `auth/${String(firebaseMessage).toLowerCase().replace(/_/g, '-')}`
      : 'auth/internal-error';
    error.status = response.status;
    error.body = body;
    throw error;
  }

  return body;
}

async function applyActionCodeViaRest(query) {
  const apiKey = query.apiKey || process.env.REACT_APP_API_KEY;
  const endpoint = `https://identitytoolkit.googleapis.com/v1/accounts:update?key=${encodeURIComponent(
    apiKey
  )}`;

  console.info('MFA confirm-removal applying action code via REST', {
    apiKey: redact(apiKey),
    oobCode: redact(query.oobCode),
    endpoint: 'https://identitytoolkit.googleapis.com/v1/accounts:update',
  });

  const response = await fetch(endpoint, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ oobCode: query.oobCode }),
  });

  const text = await response.text();
  console.info('MFA confirm-removal REST apply response', {
    ok: response.ok,
    status: response.status,
    body: text,
  });
  let body = null;

  try {
    body = JSON.parse(text);
  } catch (error) {
    body = null;
  }

  if (!response.ok) {
    const firebaseMessage = body && body.error && body.error.message;
    const error = new Error(
      firebaseMessage || text || 'Firebase request failed'
    );
    error.code = firebaseMessage
      ? `auth/${String(firebaseMessage).toLowerCase().replace(/_/g, '-')}`
      : 'auth/internal-error';
    error.status = response.status;
    error.body = body;
    throw error;
  }

  return body;
}

function getCurrentLocation(props) {
  return (props && props.location) || window.location;
}

function redirectTo(path, props) {
  if (props && props.history && typeof props.history.replace === 'function') {
    props.history.replace(path);
    return;
  }

  window.location.replace(path);
}

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
    };
  }

  static getDerivedStateFromError(error) {
    return {
      hasError: true,
      error: error,
    };
  }

  componentDidCatch(error, info) {
    console.error('MFA confirm-removal page crashed', error, info);
  }

  render() {
    if (this.state.hasError) {
      return (
        <section className='page login-page mfa-confirm-removal-page'>
          <div className='main'>
            <div className='login-form'>
              <h2>Confirm MFA removal</h2>
              <Alert severity='error' className='mfa-confirm-alert'>
                Something went wrong while loading this page.
              </Alert>
              <p className='mfa-confirm-copy'>
                Please reload the page and try the confirmation link again.
              </p>
              <div className='mfa-confirm-actions'>
                <Button
                  type='button'
                  variant='contained'
                  onClick={() => window.location.reload()}
                >
                  Reload
                </Button>
              </div>
              {this.state.error && (
                <pre className='mfa-confirm-debug'>
                  {this.state.error.message || String(this.state.error)}
                </pre>
              )}
            </div>
          </div>
        </section>
      );
    }

    return this.props.children;
  }
}

ErrorBoundary.propTypes = {
  children: PropTypes.node.isRequired,
};

function MfaConfirmRemovalPage(props = {}) {
  const [status, setStatus] = React.useState('loading');
  const [error, setError] = React.useState('');
  const [actionCodeInfo, setActionCodeInfo] = React.useState(null);
  const location = getCurrentLocation(props);
  const query = parseQuery(location);
  const sys = SYSTEM_RESOURCE;
  const systemName = process.env.REACT_APP_SYSTEM_NAME;
  const backgroundImagePath = require(`../../../images/system/${sys}/login_bg.png`);

  const mainStyle = {
    backgroundImage: `url(${backgroundImagePath})`,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
  };

  React.useEffect(function () {
    document.title =
      (process.env.REACT_APP_SYSTEM_NAME || 'AQS') + ' - Confirm MFA removal';
  }, []);

  React.useEffect(
    function () {
      let cancelled = false;

      async function loadActionCode() {
        console.info('MFA confirm-removal parsed query', {
          mode: query.mode,
          oobCode: redact(query.oobCode),
          apiKey: redact(query.apiKey),
          continueUrl: query.continueUrl,
          pathname: location.pathname,
          search: location.search,
        });

        if (!query.oobCode) {
          if (!cancelled) {
            setStatus('error');
            setError('This confirmation link is missing its action code.');
          }
          return;
        }

        setStatus('loading');
        setError('');

        try {
          const rawResponse = await fetchActionCodeData(query);
          if (rawResponse) {
            console.info('MFA confirm-removal raw response fields', {
              keys: Object.keys(rawResponse),
              requestType: rawResponse.requestType,
              mfaInfo: rawResponse.mfaInfo,
              email: rawResponse.email,
              newEmail: rawResponse.newEmail,
            });
          }

          if (cancelled) {
            return;
          }

          if (
            rawResponse.requestType !==
            ActionCodeOperation.REVERT_SECOND_FACTOR_ADDITION
          ) {
            setStatus('error');
            setError('This link is not for removing a second factor.');
            return;
          }

          setActionCodeInfo({
            operation: rawResponse.requestType,
            data: {
              email: rawResponse.email || null,
              previousEmail: rawResponse.newEmail || null,
              multiFactorInfo: rawResponse.mfaInfo || null,
            },
          });
          setStatus('ready');
        } catch (err) {
          if (cancelled) {
            return;
          }
          logFirebaseError('Failed to validate MFA removal code', err);
          setStatus('error');
          setError(friendlyError(err));
        }
      }

      loadActionCode();

      return function () {
        cancelled = true;
      };
    },
    [location.pathname, location.search]
  );

  const continuePath = safeContinuePath(query.continueUrl) || '/login';
  const data = actionCodeInfo && actionCodeInfo.data;
  const factorInfo = pickMultiFactorInfo(actionCodeInfo);
  const factorLabel = summarizeFactor(factorInfo);

  const handleCancel = function () {
    redirectTo(continuePath, props);
  };

  const handleConfirm = async function () {
    if (status !== 'ready') {
      return;
    }

    setStatus('applying');
    setError('');

    try {
      console.info('MFA confirm-removal applying action code', {
        oobCode: redact(query.oobCode),
      });
      await applyActionCodeViaRest(query);
      console.info('MFA confirm-removal applyActionCode succeeded');
      setStatus('success');
    } catch (err) {
      logFirebaseError('Failed to apply MFA removal code', err);
      setStatus('error');
      setError(friendlyError(err));
    }
  };

  return (
    <section className='page login-page mfa-confirm-removal-page'>
      <Favicon url={require(`./../../../images/system/${sys}/favicon_1.png`)} />

      <div className='main' style={mainStyle}>
        <div className='login-form form-horizontal'>
          <header>
            <div className='logo'>{systemName}</div>
            <div className='version'>
              {`v.`} <strong>{misc.versionNumber}</strong>
            </div>
          </header>

          <h2 className='mfa-confirm-title'>Confirm MFA removal</h2>

          {status === 'loading' && (
            <div className='mfa-confirm-loading'>
              <CircularProgress size={24} />
              <span>Checking this link...</span>
            </div>
          )}

          {status !== 'loading' && error && (
            <Alert severity='error' className='mfa-confirm-alert'>
              {error}
            </Alert>
          )}

          {status === 'ready' && (
            <>
              <Alert
                severity='warning'
                className='mfa-confirm-alert'
                style={{ fontSize: '1.25rem' }}
              >
                This will remove the multi-factor used to sign in to the
                account:
                <br />
                <b> {data?.email}</b>.
              </Alert>
              <p className='mfa-confirm-copy'>
                Remove <strong>{factorLabel}</strong> as a sign-in method?
              </p>
              <p className='mfa-confirm-copy mfa-confirm-muted'>
                This only happens after you click Confirm. The page does not
                apply the change automatically.
              </p>
              <div className='mfa-confirm-actions'>
                <Button
                  type='button'
                  variant='contained'
                  color='error'
                  disabled={status !== 'ready'}
                  onClick={handleConfirm}
                >
                  Confirm
                </Button>
                <Button
                  type='button'
                  variant='outlined'
                  disabled={status === 'applying'}
                  onClick={handleCancel}
                >
                  Cancel
                </Button>
              </div>
            </>
          )}

          {status === 'applying' && (
            <div className='mfa-confirm-loading'>
              <CircularProgress size={24} />
              <span>Removing this factor...</span>
            </div>
          )}

          {status === 'success' && (
            <>
              <Alert severity='success' className='mfa-confirm-alert'>
                MFA removal completed successfully.
              </Alert>
              <p className='mfa-confirm-copy'>
                You can return to the app or close this tab.
              </p>
              <div className='mfa-confirm-actions'>
                <Button
                  type='button'
                  variant='contained'
                  onClick={handleCancel}
                >
                  Continue
                </Button>
              </div>
            </>
          )}

          {status === 'error' && (
            <div className='mfa-confirm-actions'>
              <Button type='button' variant='contained' onClick={handleCancel}>
                Dismiss
              </Button>
            </div>
          )}

          {/* <p className='mfa-confirm-footer'>
            If this link came from Firebase MFA, update the Firebase Console
            email template so its action URL points to this route.
          </p> */}
        </div>
      </div>
    </section>
  );
}

MfaConfirmRemovalPage.propTypes = {
  history: PropTypes.object,
  location: PropTypes.object,
};

export default function MfaConfirmRemoval(props) {
  return (
    <ErrorBoundary>
      <MfaConfirmRemovalPage {...props} />
    </ErrorBoundary>
  );
}
